import { observable, computed } from 'mobx'
import { Transaction } from '@odiupsk/up-api-client'
import { cardsApi } from '../../common/api'
import { TablePaginationStore } from '../../common/modules/data-table/stores/TablePaginationStore'
import { FilterExpressionBuilder } from '../../common/utils/filterExpressionBuilder'
import { TableSortStore } from '../../common/modules/data-table/stores/TableSortStore'
import { logError } from '../../common/api/utils'
import { errorDuringFetch } from '../../common/utils/i18n/alerts'

/**
 * map Transaction to type for table
 * @param t
 */
const transform = (t: Transaction) => {
  return {
    date_date: t.date_date ? new Date(t.date_date) : undefined,
    transaction_type: t.transaction_type!,
    'transaction_type.name': t.transaction_type!,
    main_description: t.main_description,
    isStorno: t.transaction_type!.code === 'REDR',
    isAccepted: t.transaction_type!.code === 'TRAU',
    notAccepted: t.transaction_type!.code === 'TRNA',
    // TODO: test if this does not break sorting (sorting number vs sorting string)
    amount: t.amount!,
    // currency: t.currency,
    id: t.id,
    actions: null,
    raw: t,
  }
}

export type TTransactionTableRow = ReturnType<typeof transform>

export class TransactionsTableStore {
  @observable.ref private _data?: TTransactionTableRow[]

  @observable loading: boolean = false

  pagination = new TablePaginationStore({ perPage: 10 })

  filters = new FilterExpressionBuilder<
    'search' | 'date_date' | 'place' | 'price' | 'transactionType'
  >(
    [
      ['search', ''],
      ['date_date', ''],
      ['place', ''],
      ['price', ''],
      ['transactionType', ''],
    ],
    `\${search}&&\${date_date}&&\${place}&&\${price}&&\${transactionType}`
  )

  sorting = new TableSortStore([['date_date', 'desc']])

  selectedCardId?: string

  setData(data: Transaction[]) {
    this._data = data.map(i => transform(i))
  }

  @computed get data() {
    return this._data || []
  }

  @computed get isLoaded() {
    return !!this._data
  }

  async fetchTransactions(params?: this['params']) {
    if (!this.selectedCardId) return
    this.loading = true
    try {
      const res = await cardsApi.getCardTransactions(this.selectedCardId, {
        params,
      })
      this.setData(res.data.data!)
      this.pagination.objectCount = res.data.pagination.object_count || 0
    } catch (err) {
      logError(err, errorDuringFetch)
      this.loading = false
      throw err
    }
    this.loading = false
  }

  @computed get watch() {
    return [
      this.pagination.page,
      this.pagination.perPage,
      this.sorting.sortDefsAsRecord,
      this.filters.querystring,
    ]
  }

  get params() {
    return {
      ...this.sorting.params,
      ...this.pagination.params,
      ...this.filters.params,
    }
  }

  @computed get isFilteredAndNoData() {
    return this.filters.isAnyFilterActive && this.data.length === 0
  }
}
